import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { filterCanceledUsers } from 'lib/util'
import { SiteUserTable } from './component.styles'
import {
  NAME,
  ELEMENTARY_COMPLETION,
  ELEMENTARY_TRAINING,
  EXPERIENCE,
  HIGH_SCHOOL_TRAINING,
  MIDDLE_SCHOOL_COMPLETION,
  MIDDLE_SCHOOL_TRAINING,
  TEACHES_LESSONS,
  TOTAL_ELEM_TRAININGS,
  TOTAL_MS_TRAININGS,
  TOTAL_DWB_TRAININGS,
  DWB_COLUMN_WIDTHS,
  DWB_PROGRAM_DISABLED_MARK,
  VARIANT_K8_CORE,
  VARIANT_K12_DWB,
} from './constants'
import { trainingCompletionStatus } from './helpers'

const teachesLessonsDefined = row =>
  row?.flatPreferences?.teachesLessons !== undefined

const DEFAULT_DWB_COLUMN_ENABLED = {
  elem: true,
  ms: true,
  hs: true,
}

const SiteUserTrainingGrid = ({
  className,
  dwbColumnEnabled,
  users,
  variant,
}) => {
  const columns = useMemo(() => {
    if (variant === VARIANT_K12_DWB) {
      const enabled = {
        ...DEFAULT_DWB_COLUMN_ENABLED,
        ...dwbColumnEnabled,
      }

      const dwbCell = (programEnabled, count, row) =>
        programEnabled
          ? trainingCompletionStatus(
              count,
              TOTAL_DWB_TRAININGS,
              teachesLessonsDefined(row),
            )
          : DWB_PROGRAM_DISABLED_MARK

      const dwbSort = (programEnabled, count, row) => {
        if (!programEnabled) {
          return -2
        }
        return teachesLessonsDefined(row) ? count ?? 0 : -1
      }

      return [
        {
          title: NAME,
          fieldValue: row => {
            return <b>{`${row.firstName} ${row.lastName}`}</b>
          },
          sortValue: row => row.firstName,
        },
        {
          title: ELEMENTARY_TRAINING,
          fieldValue: row =>
            dwbCell(enabled.elem, row?.trainingCompletion?.dwb?.elem, row),
          unbold: true,
          sortValue: row =>
            dwbSort(enabled.elem, row?.trainingCompletion?.dwb?.elem, row),
        },
        {
          title: MIDDLE_SCHOOL_TRAINING,
          fieldValue: row =>
            dwbCell(enabled.ms, row?.trainingCompletion?.dwb?.ms, row),
          unbold: true,
          sortValue: row =>
            dwbSort(enabled.ms, row?.trainingCompletion?.dwb?.ms, row),
        },
        {
          title: HIGH_SCHOOL_TRAINING,
          fieldValue: row =>
            dwbCell(enabled.hs, row?.trainingCompletion?.dwb?.hs, row),
          unbold: true,
          sortValue: row =>
            dwbSort(enabled.hs, row?.trainingCompletion?.dwb?.hs, row),
        },
      ]
    }

    return [
      {
        title: NAME,
        fieldValue: row => {
          return <b>{`${row.firstName} ${row.lastName}`}</b>
        },
        sortValue: row => row.firstName,
      },
      {
        title: TEACHES_LESSONS,
        fieldValue: row =>
          row?.flatPreferences?.teachesLessons
            ? 'Yes'
            : row?.flatPreferences?.teachesLessons === undefined
            ? 'No role set'
            : 'No',
        unbold: true,
        sortValue: row =>
          row?.flatPreferences?.teachesLessons
            ? 1
            : row?.flatPreferences?.teachesLessons === undefined
            ? 3
            : 2,
      },
      {
        title: EXPERIENCE,
        fieldValue: row => row?.flatPreferences?.experience || '—',
        unbold: true,
      },
      {
        title: ELEMENTARY_COMPLETION,
        fieldValue: row =>
          row?.flatPreferences?.teachesLessons !== undefined
            ? `${row?.trainingCompletion?.elem}/${TOTAL_ELEM_TRAININGS}`
            : '—',
        unbold: true,
      },
      {
        title: MIDDLE_SCHOOL_COMPLETION,
        fieldValue: row =>
          row?.flatPreferences?.teachesLessons !== undefined
            ? `${row?.trainingCompletion?.ms}/${TOTAL_MS_TRAININGS}`
            : '—',
        unbold: true,
      },
    ]
  }, [variant, dwbColumnEnabled])

  const defaultSort =
    variant === VARIANT_K12_DWB
      ? { columnTitle: NAME, direction: 'asc' }
      : {
          columnTitle: TEACHES_LESSONS,
          direction: 'asc',
        }

  const numOfColumns = variant === VARIANT_K12_DWB ? 4 : 7

  return (
    <SiteUserTable
      className={className}
      columnWidths={variant === VARIANT_K12_DWB ? DWB_COLUMN_WIDTHS : undefined}
      columns={columns}
      defaultSort={defaultSort}
      filter={false}
      headThemeLight
      numOfColumns={numOfColumns}
      rows={filterCanceledUsers(users)}
      sort
    />
  )
}

SiteUserTrainingGrid.propTypes = {
  className: PropTypes.string,
  dwbColumnEnabled: PropTypes.shape({
    elem: PropTypes.bool,
    hs: PropTypes.bool,
    ms: PropTypes.bool,
  }),
  users: PropTypes.arrayOf(PropTypes.object),
  variant: PropTypes.oneOf([VARIANT_K8_CORE, VARIANT_K12_DWB]),
}

SiteUserTrainingGrid.defaultProps = {
  dwbColumnEnabled: undefined,
  variant: VARIANT_K8_CORE,
}

export default SiteUserTrainingGrid
